import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { useFormattingLocales } from '../../utils/getFormattingLocales';
import { Button } from '../buttons/Button';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';

import { DeleteChatModal } from './DeleteChatModal';
import { formatChatListActivity } from './formatChatListActivity';
import { groupChatsByRecency } from './groupChatsByRecency';

import styles from './ChatList.module.scss';

const SECTION_TITLE_KEYS = {
  today: 'app.aiAssistant.chatListSectionToday',
  yesterday: 'app.aiAssistant.chatListSectionYesterday',
  last7Days: 'app.aiAssistant.chatListSectionLast7Days',
  last30Days: 'app.aiAssistant.chatListSectionLast30Days',
  older: 'app.aiAssistant.chatListSectionOlder',
} as const;

export type AiAssistantChatSummary = {
  id: string;
  createdAt: number;
  title: string;
  updatedAt: number;
};

export type ChatListProps = {
  activeChatId?: string | null;
  chats: AiAssistantChatSummary[];
  className?: string;
  onActiveChatChange?: (chatId: string) => void;
  onCreateChat?: () => void;
  onDeleteChat?: (chatId: string) => void;
};

export function ChatList({
  activeChatId,
  chats,
  className,
  onActiveChatChange,
  onCreateChat,
  onDeleteChat,
}: ChatListProps) {
  const { t } = useTranslation();
  const locales = useFormattingLocales();
  const [pendingDeleteChatId, setPendingDeleteChatId] = useState<string | null>(
    null
  );
  const groupedChats = groupChatsByRecency(chats);
  const pendingDeleteChatTitle =
    chats.find((chat) => chat.id === pendingDeleteChatId)?.title ?? '';

  function confirmDeleteChat() {
    if (pendingDeleteChatId) {
      onDeleteChat?.(pendingDeleteChatId);
    }
    setPendingDeleteChatId(null);
  }

  return (
    <>
      <div
        className={clsx(styles['root'], className)}
        data-testid="ai-assistant-chat-list"
      >
        <Button
          className={styles['new-chat']}
          icon={<GoogleFontIcon name="add" solid />}
          onClick={() => onCreateChat?.()}
          variant="secondary"
          data-testid="ai-assistant-chat-list-new"
        >
          {t('app.aiAssistant.newChat')}
        </Button>

        {groupedChats.map(({ bucket, chats: sectionChats }) => (
          <section
            aria-labelledby={`ai-assistant-chat-list-${bucket}`}
            className={styles['section']}
            key={bucket}
            data-testid={`ai-assistant-chat-list-section-${bucket}`}
          >
            <h2
              className={styles['section__title']}
              id={`ai-assistant-chat-list-${bucket}`}
            >
              {t(SECTION_TITLE_KEYS[bucket])}
            </h2>
            {sectionChats.map((chat) => {
              const activity = formatChatListActivity(chat, locales);

              return (
                <div
                  className={clsx(styles['item'], {
                    [styles['item--active']]: chat.id === activeChatId,
                  })}
                  key={chat.id}
                  data-testid={`ai-assistant-chat-list-item-${chat.id}`}
                >
                  <button
                    className={styles['item__main']}
                    data-testid={`ai-assistant-chat-list-open-${chat.id}`}
                    onClick={() => onActiveChatChange?.(chat.id)}
                    type="button"
                  >
                    <span className={styles['item__title']}>{chat.title}</span>
                    <span
                      className={styles['item__dates']}
                      title={activity}
                      data-testid={`ai-assistant-chat-list-dates-${chat.id}`}
                    >
                      {activity}
                    </span>
                  </button>
                  <Button
                    className={styles['item__delete']}
                    icon={<GoogleFontIcon name="delete" solid />}
                    onClick={() => setPendingDeleteChatId(chat.id)}
                    title={t('app.aiAssistant.deleteChat')}
                    variant="text"
                    data-testid={`ai-assistant-chat-list-delete-${chat.id}`}
                  />
                </div>
              );
            })}
          </section>
        ))}
      </div>
      {pendingDeleteChatId ? (
        <DeleteChatModal
          chatTitle={pendingDeleteChatTitle}
          onClose={() => setPendingDeleteChatId(null)}
          onConfirm={confirmDeleteChat}
        />
      ) : null}
    </>
  );
}
